import { useMemo } from 'react';

import { Box } from '@aicon-dev/caas-core';
import { Button } from '@aicon-dev/caas-ui';
import { useSetState } from 'ahooks';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FixedBottomBar } from '~/components/ui/FixedBottomBar';
import { Header } from '~/components/ui/Header';

import { ReportListFilterType } from '../ReportList/ReportList.types';

import { RadioButtonSelect } from './ReportListFilter.Period';
import { ProcessSelect } from './ReportListFilter.Process';
import { RangePicker } from './ReportListFilter.Range';

export interface ReportListFilterProps {
  filter: ReportListFilterType;
  onClose: () => void;
  onDone: (value: ReportListFilterType) => void;
}

export const ReportListFilter = (props: ReportListFilterProps) => {
  const { filter, onClose, onDone } = props;
  const { t } = useTranslation('report');
  const [filterState, setFilterState] = useSetState<ReportListFilterType>(filter);

  const periodType = useMemo(() => {
    if (!filterState.startWorkDate) return '';
    const today = dayjs();
    if (!today.isSame(filterState.endWorkDate, 'day')) return '';
    if (today.diff(filterState.startWorkDate, 'day') === 7) return '1-week';
    if (today.diff(filterState.startWorkDate, 'month') === 1) return '1-month';
    if (today.diff(filterState.startWorkDate, 'month') === 3) return '3-month';
    if (today.diff(filterState.startWorkDate, 'month') === 6) return '6-month';
    return '';
  }, [filterState.startWorkDate, filterState.endWorkDate]);

  const handleDone = () => {
    onDone(filterState);
  };

  const periodSelectOptions = [
    {
      label: t('report:filter_period_one_week'),
      value: '1-week',
    },
    {
      label: t('report:filter_period_one_month'),
      value: '1-month',
    },
    {
      label: t('report:filter_period_three_months'),
      value: '3-month',
    },
    {
      label: t('report:filter_period_six_months'),
      value: '6-month',
    },
  ];

  return (
    <>
      <Header
        onBack={onClose}
        right={
          <Header.TextButton onClick={handleDone}>{t('report:filter_apply')}</Header.TextButton>
        }
      >
        <span>{t('report:filter_title')}</span>
      </Header>
      <Box p="28px 1rem">
        <Label>{t('report:filter_period')}</Label>
        <RadioButtonSelect
          value={periodType}
          options={periodSelectOptions}
          onChange={(value) => {
            const today = dayjs();
            switch (value) {
              case '1-week':
                setFilterState({
                  endWorkDate: today.format(),
                  startWorkDate: today.subtract(7, 'day').format(),
                });
                break;
              case '1-month':
                setFilterState({
                  endWorkDate: today.format(),
                  startWorkDate: today.subtract(1, 'month').format(),
                });
                break;
              case '3-month':
                setFilterState({
                  endWorkDate: today.format(),
                  startWorkDate: today.subtract(3, 'month').format(),
                });
                break;
              case '6-month':
                setFilterState({
                  endWorkDate: today.format(),
                  startWorkDate: today.subtract(6, 'month').format(),
                });
                break;
              default:
                break;
            }
          }}
        />

        <Box mb={28}>
          <RangePicker
            value={[filterState.startWorkDate, filterState.endWorkDate]}
            startPlaceholder={t('report:filter_period_start_date').toString()}
            endPlaceholder={t('report:filter_period_end_date').toString()}
            onChange={(value) =>
              setFilterState({
                startWorkDate: value[0],
                endWorkDate: value[1],
              })
            }
          />
        </Box>

        <Label>{t('report:filter_process')}</Label>
        <ProcessSelect
          value={
            Array.isArray(filterState.processes)
              ? filterState.processes || []
              : filterState.processes
              ? [filterState.processes]
              : []
          }
          onChange={(value) =>
            setFilterState({
              processes: value,
            })
          }
        />

        {/* <Box pt="56px">
          <Button block color="primary" size="large" variant="filled" onClick={handleDone}>
            {t('report:filter_apply')}
          </Button>
        </Box> */}

        <FixedBottomBar>
          <Button block color="primary" size="large" variant="filled" onClick={handleDone}>
            {t('report:filter_apply')}
          </Button>
        </FixedBottomBar>
      </Box>
    </>
  );
};

const Label = styled.div`
  margin-bottom: 8px;
`;
